import React, { createContext, useContext, useReducer, useEffect, useState, useRef } from 'react';
import { authService } from '../services/authService';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchCurrentUserWithRateLimitRetry = async (maxAttempts = 3) => {
  let lastError;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      return await authService.getCurrentUser();
    } catch (error) {
      lastError = error;
      if (error.response?.status === 429 && attempt < maxAttempts - 1) {
        const delaySec = error.retryAfterSeconds || 3;
        await sleep(delaySec * 1000);
        continue;
      }
      throw error;
    }
  }
  throw lastError;
};
const AuthContext = createContext();

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: {
          ...state.user,
          ...action.payload,
          // Deep merge vendor_profile if it exists
          vendor_profile: action.payload.vendor_profile 
            ? { ...state.user?.vendor_profile, ...action.payload.vendor_profile }
            : state.user?.vendor_profile
        },
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [isInitializing, setIsInitializing] = useState(false);
  const isLoggingOut = useRef(false);

  // Check for existing token on app load
  useEffect(() => {
    const initAuth = async () => {
      // Prevent multiple simultaneous calls
      if (isInitializing) return;
      setIsInitializing(true);

      const token = localStorage.getItem('token');
      if (token) {
        try {
          const user = await fetchCurrentUserWithRateLimitRetry();
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: { user, token },
          });
        } catch (error) {
          if (error.response?.status === 429) {
            // Transient — keep token so user is not logged out during bursts
            dispatch({
              type: 'AUTH_SUCCESS',
              payload: { user: null, token },
            });
          } else if (error.response?.status === 401) {
            // Token is invalid or expired
            localStorage.removeItem('token');
            dispatch({
              type: 'AUTH_FAILURE',
              payload: 'Session expired. Please login again.',
            });
          } else {
            // Other errors
            dispatch({
              type: 'AUTH_FAILURE',
              payload: 'Authentication failed. Please try again.',
            });
          }
        }
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: null });
      }
      
      setIsInitializing(false);
    };

    initAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for token expiration periodically
  useEffect(() => {
    if (!state.token) return;

    const checkTokenExpiration = () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          // Decode JWT token to check expiration
          const payload = JSON.parse(atob(token.split('.')[1]));
          const currentTime = Math.floor(Date.now() / 1000);
          
          if (payload.exp < currentTime) {
            // Token is expired
            localStorage.removeItem('token');
            dispatch({
              type: 'AUTH_FAILURE',
              payload: 'Session expired. Please login again.',
            });
          }
        }
      } catch (error) {
        // Invalid token format
        localStorage.removeItem('token');
        dispatch({
          type: 'AUTH_FAILURE',
          payload: 'Invalid session. Please login again.',
        });
      }
    };

    // Check every 5 minutes
    const interval = setInterval(checkTokenExpiration, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [state.token]);

  const login = async (credentials) => {
    // Don't set global loading state during login to avoid hiding the form
    try {
      const response = await authService.login(credentials);

      if (response?.data?.requires_two_factor) {
        return {
          success: false,
          requiresTwoFactor: true,
          challengeToken: response.data.challenge_token,
          email: response.data.email,
        };
      }

      const { user, access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token: access_token },
      });
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      dispatch({
        type: 'AUTH_FAILURE',
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  const verifyTwoFactorLogin = async ({ challengeToken, code }) => {
    try {
      const response = await authService.verifyTwoFactor({
        challenge_token: challengeToken,
        code,
      });
      const { user, access_token } = response.data;

      localStorage.setItem('token', access_token);
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token: access_token },
      });

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Invalid authentication code';
      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    // dispatch({ type: 'AUTH_START' });
    // Don't set global loading state during registration to avoid hiding the form
    // The individual components will handle their own loading states
    try {
      const response = await authService.register(userData);
      const { user, access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token: access_token },
      });
      
      return { success: true };
    } catch (error) {
      // Handle validation errors from backend
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        dispatch({
          type: 'AUTH_FAILURE',
          payload: 'Validation failed',
        });
        return { success: false, error: validationErrors };
      } else {
        const errorMessage = error.response?.data?.message || 'Registration failed';
        dispatch({
          type: 'AUTH_FAILURE',
          payload: errorMessage,
        });
        return { success: false, error: errorMessage };
      }
    }
  };

  const oauthLogin = async (oauthData, userType = 'client') => {
    try {
      // Use OAuth endpoint which handles both login and registration
      const response = await authService.oauthLogin({
        provider: oauthData.provider,
        provider_id: oauthData.provider_id,
        email: oauthData.email,
        first_name: oauthData.first_name,
        last_name: oauthData.last_name,
        profile_image_url: oauthData.profile_image_url,
        user_type: userType,
        access_token: oauthData.access_token,
      });

      if (response?.data?.requires_two_factor) {
        return {
          success: false,
          requiresTwoFactor: true,
          challengeToken: response.data.challenge_token,
          email: response.data.email,
        };
      }

      const { user, access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token: access_token },
      });
      
      return { success: true };
    } catch (error) {
      // Handle different error types
      if (error.response?.status === 409) {
        // Conflict - user exists with different registration method
        const errorMessage = error.response?.data?.message || 'An account with this email already exists. Please use email/password to sign in.';
        dispatch({
          type: 'AUTH_FAILURE',
          payload: errorMessage,
        });
        return { success: false, error: errorMessage };
      } else if (error.response?.status === 422 && error.response?.data?.errors) {
        // Validation errors
        const validationErrors = error.response.data.errors;
        dispatch({
          type: 'AUTH_FAILURE',
          payload: 'Validation failed',
        });
        return { success: false, error: validationErrors };
      } else {
        // Other errors
        const errorMessage = error.response?.data?.message || 'OAuth login failed';
        dispatch({
          type: 'AUTH_FAILURE',
          payload: errorMessage,
        });
        return { success: false, error: errorMessage };
      }
    }
  };

  const logout = async () => {
    // Prevent multiple simultaneous logout calls
    if (isLoggingOut.current) {
      return;
    }
    
    isLoggingOut.current = true;
    
    try {
      // Call the backend logout endpoint to deactivate sessions
      await authService.logout();
    } catch (error) {
      // Even if logout fails on backend, we should still clear local state
      // Don't log warning if it's a 401 (token already invalid/expired)
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        console.warn('Logout API call failed:', error);
      }
    } finally {
      // Always clear local storage and state
      localStorage.removeItem('token');
      dispatch({ type: 'LOGOUT' });
      
      // Dispatch event to notify other components that a user has logged out
      // Only dispatch if we actually had a user logged in
      if (state.user) {
        window.dispatchEvent(new CustomEvent('userLoggedOut', { 
          detail: { userId: state.user?.id, timestamp: Date.now() }
        }));
      }
      
      // Reset the flag after a short delay to allow cleanup
      setTimeout(() => {
        isLoggingOut.current = false;
      }, 1000);
    }
  };

  const updateUser = (userData) => {
    dispatch({
      type: 'UPDATE_USER',
      payload: userData,
    });
  };

  const hasRole = (role) => {
    return state.user?.user_type === role;
  };

  const hasAnyRole = (roles) => {
    return roles.includes(state.user?.user_type);
  };

  const value = {
    ...state,
    login,
    verifyTwoFactorLogin,
    register,
    oauthLogin,
    logout,
    updateUser,
    hasRole,
    hasAnyRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
